import React, { useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { createStructuredSelector } from 'reselect';
import { get, isEmpty, isNil } from 'lodash';
import { connect, useSelector } from 'react-redux';
import styled from 'styled-components';

import messages from 'containers/PreOpeningPage/messages';
import { translate } from 'containers/services/translate';
import ClaimDeclaration, {
  claimDeclarationValidator,
} from 'containers/PreOpeningPage/ClaimDeclaration';
import NavigationButtons from 'components/navigationButtons';
import ClaimInformation, {
  claimInformationValidator,
} from 'containers/PreOpeningPage/ClaimInformation';
import OpposingParty from 'containers/PreOpeningPage/OpposingParty/OpposingParty';
import {
  fraudInfoValidator,
  opposingPartyValidator,
} from 'containers/PreOpeningPage/OpposingParty/utils';
import ClaimCircumstances, {
  claimCircumstancesValidator,
} from 'containers/PreOpeningPage/ClaimCircumstances';
import VictimCard from 'containers/PreOpeningPage/VictimCard';
import { victimsValidator } from 'containers/PreOpeningPage/utils';
import { PAGES } from 'routes';
import routesHelper from 'containers/services/routes-helper';
import { makeSelectListings } from 'containers/AppContainer/selectors';
import { Listings } from 'containers/AppContainer/typings';
import { setClaim } from 'containers/PreOpeningPage/actions';
import {
  makeSelecClaimToPreopen,
  makeSelectIsProvisionalClaimExist,
} from 'containers/PreOpeningPage/selectors';
import {
  makeSelectSearchClaims,
  makeSelectedClaims,
} from 'containers/IdentificationPage/selectors';
import Stepper from 'containers/AppContainer/Stepper';
import {
  checkClassVictims,
  checkVictimsAge,
  extractOpposingParties,
  formatClaimToPreOpen,
  checkTotalVictims,
} from 'containers/PreOpeningPage/utils';
import { resolveSagisClaim } from 'helpers/utils';
import {
  formsAreValidated,
  listOfFormsAreValidated,
} from 'containers/services/validateForm';
import { VictimType } from 'typings/globalTypes';

import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import FraudeInformation from 'containers/PreOpeningPage/fraude';
import {
  makeSelecClaimRecap,
  makeSelectCorporealLawyers,
} from 'containers/RecapManagerPage/selectors';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { isUniqueCinVictims } from 'containers/PreOpeningPage/utils';
import {
  CHOICE,
  VICTIM_ITT_AVAILABILITY,
} from 'containers/PreOpeningPage/constants';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import { CLAIM, VICTIM } from 'containers/data-structure';
import Alert from '@material-ui/lab/Alert';
import useDeepCompareEffect from 'use-deep-compare-effect';
const claimFormValidator = (claimToPreopen: any) => ({
  claimCircumstances: claimCircumstancesValidator(get(claimToPreopen, 'claim')),
  claimInformations: claimInformationValidator(claimToPreopen),
  claimDeclaration: claimDeclarationValidator(get(claimToPreopen, 'driver')),
  victims: victimsValidator(get(claimToPreopen, 'victims')),
  opposingParty: opposingPartyValidator(get(claimToPreopen, 'opposingParty')),
  fraudInfo: fraudInfoValidator(get(claimToPreopen, 'fraudInfo')),
});

const claimFormValidatorOpeningByUsage = (claimToPreopen: any) => ({
  claimCircumstances: claimCircumstancesValidator(get(claimToPreopen, 'claim')),
  claimInformations: claimInformationValidator(claimToPreopen),
});
export const globalValidator = {
  claimFormValidator,
  claimFormValidatorOpeningByUsage,
};

export interface PreOpeningPageProps {
  intl: IntlShape;
  listings: Listings;
  setClaim: Function;
  storedClaimToPreopen: any;
  policyInfo: { sagis: {}; lossDate: Date };
  selectedClaim: any;
  corporealLawyers: any;
  location?: any;
}

const StyledDiv = styled.div`
  height: 24px;
  text-align: left;
  font-family: Roboto;
  letter-spacing: 0;
  color: #384164;
  opacity: 1;
  font-size: 20px;
  margin-top: 50px;
  margin-bottom: 40px;
  font-weight: 100;
`;
export const PreOpeningPage: React.FC<PreOpeningPageProps> = (
  props,
): JSX.Element => {
  const isPreOpeningManager = userPrivileges.userHasPrivilege(
    PRIVILEGES.ISPREOPENMANAGER,
  );
  const [procedureType, setProcedureType] = useState(
    get(props, 'location.state.procedureType')
      ? get(props, 'location.state.procedureType')
      : PROCEDURE_TYPES.FIRST_INSTANCE,
  );
  const isUsageOpening =
    featureService.hasFeature(FEATURES.OPENING_BY_USAGE) &&
    isPreOpeningManager &&
    get(props, 'location.state.isUsageOpening', false);

  const isProvisionalClaimExist = useSelector(
    makeSelectIsProvisionalClaimExist(),
  );

  const hasCourtCalendarFeature = featureService.hasFeature(
    FEATURES.COURT_CALENDAR,
  );
  const [isSearchExecuted, setIsSearchExecuted] = useState(false);
  const [
    isProvisionalReferenceAlertOpenned,
    setIsProvisionalReferenceAlertOpenned,
  ] = useState(false);

  const handleChangeProcedureType = (
    event: React.ChangeEvent<{ name?: string; value: any }>,
  ) => {
    const { value } = event.target;
    setProcedureType(value);
  };
  const [procedureNatureId, setProcedureNatureId] = useState(
    get(props, 'location.state.procedureNatureId')
      ? get(props, 'location.state.procedureNatureId')
      : 3,
  );

  const handleChangeProcedureNature = (
    event: React.ChangeEvent<{ name?: string; value: any }>,
  ) => {
    const { value } = event.target;
    setProcedureNatureId(value);
  };
  React.useEffect(() => {
    if (
      (isEmpty(props.policyInfo) ||
        isEmpty(props.policyInfo.sagis) ||
        !get(props.policyInfo, 'lossDate')) &&
      !get(props, 'location.state.isFromSagis') &&
      isNil(get(props, 'location.state.claimId', null))
    ) {
      isPreOpeningManager
        ? routesHelper.pushTo(PAGES.PREOPEN_MANAGER_HOME)()
        : routesHelper.pushTo(PAGES.LAWYER_HOME)();
    }
  }, [props.policyInfo, props.policyInfo.sagis, props.policyInfo.lossDate]);

  const [claimToPreopen, setClaimToPreopen] = useState({
    claim: {},
    driver: {},
    vehicule: {},
    victims: [],
    documents: get(props.storedClaimToPreopen, 'documents', []),
    opposingParty: {
      hasMaterialDammage: null,
      vehicules: [],
    },
    isUsageOpening: false,
  });
  const claimRecap = useSelector(makeSelecClaimRecap());
  useDeepCompareEffect(() => {
    if (
      get(props, 'location.state.isUsageOpening', false) &&
      !isNil(get(props, 'location.state.claimId', null)) &&
      !isEmpty(claimRecap)
    ) {
      const claimToPreOpen = {
        claim: {
          ...get(claimRecap, 'claim', {}),
          [CLAIM.MANAGEMENT_MODE]: get(
            claimRecap,
            'claim.managementModeKey',
            '',
          ),
          [CLAIM.SCALE_CASE_KEY]: String(
            get(claimRecap, 'claim.scaleCaseKey', ''),
          ),
          [CLAIM.SUSPICIOUS]: !isEmpty(get(claimRecap, 'claim.fraudInfo', []))
            ? 'YES'
            : 'NO',
        },
        driver: !isEmpty(get(claimRecap, 'driver', {}))
          ? { ...get(claimRecap, 'driver', {}), isDriverTheInsured: CHOICE.NO }
          : {
              isDriverTheInsured: CHOICE.YES,
            },
        vehicule: get(claimRecap, 'vehicules', {}),
        victims: get(claimRecap, 'victims', []).map((el: any) => ({
          ...el,
          [VICTIM.ITT_AVAILABILITY]: !isNil(el.itt)
            ? VICTIM_ITT_AVAILABILITY.AVAILABLE
            : VICTIM_ITT_AVAILABILITY.NOT_AVAILABLE,
        })),
        documents: get(claimRecap, 'documents', []),
        fraudInfo: !isEmpty(get(claimRecap, 'claim.fraudInfo', []))
          ? {
              ...get(claimRecap, 'claim.fraudInfo.[0]', {}),
              victims: get(claimRecap, 'victims', [])
                .filter((v: any) =>
                  get(claimRecap, 'claim.fraudInfo.[0].fraudVictims', [])
                    .map((el: any) => el.victimId)
                    .includes(v.id),
                )
                .map((v: any) => v.cin),
              vehicules: get(claimRecap, 'vehicules', [])
                .filter((v: any) =>
                  get(claimRecap, 'claim.fraudInfo.[0].fraudVehicules', [])
                    .map((el: any) => el.vehiculeId)
                    .includes(v.id),
                )
                .map((v: any) => v.registrationNumber),
              fraudMotifs: get(
                claimRecap,
                'claim.fraudInfo[0].fraudInfoMotif',
                [],
              ).map((el: any) => el.motifId),
            }
          : {},
        opposingParty: get(claimRecap, 'opposingParty', {
          hasMaterialDammage: !isEmpty(get(claimRecap, 'vehicules', []))
            ? 'YES'
            : false,
          vehicules: get(claimRecap, 'vehicules', []),
        }),
      };
      setClaimToPreopen({ ...claimToPreOpen, isUsageOpening: isUsageOpening });
      props.setClaim({ ...claimToPreOpen, isUsageOpening: isUsageOpening });
    }
  }, [claimRecap]);
  React.useEffect(() => {
    if (!isSearchExecuted) {
      const claimToPreOpen = !get(props, 'location.state.isFromSagis')
        ? {
            claim: get(props.storedClaimToPreopen, 'claim', {}),
            driver: get(props.storedClaimToPreopen, 'driver', {}),
            vehicule: get(props.storedClaimToPreopen, 'vehicule', {}),
            victims: get(props.storedClaimToPreopen, 'victims', []),
            documents: get(props.storedClaimToPreopen, 'documents', []),
            fraudInfo: get(props.storedClaimToPreopen, 'fraudInfo', []),
            opposingParty: get(props.storedClaimToPreopen, 'opposingParty', {
              hasMaterialDammage: null,
              vehicules: [],
            }),
          }
        : formatClaimToPreOpen(
            get(props, 'location.state.sagisInfoClaim', {}),
            props.listings,
          );
      setClaimToPreopen({ ...claimToPreOpen, isUsageOpening: isUsageOpening });
    }
  }, [props.storedClaimToPreopen]);

  const [claimValidation, setClaimValidation] = React.useState({});

  const handleChange = (claimToPreopen: any) => {
    setClaimToPreopen(claimToPreopen);
  };

  const [isOpenAlert, setIsOpenAlert] = React.useState(false);

  const handleNextPageClick = () => {
    setIsSearchExecuted(false);
    props.setClaim(claimToPreopen);

    if (
      !checkTotalVictims(
        // @ts-ignore
        parseInt(claimToPreopen.claim[CLAIM.TOTAL_INJURED_VICTIMS]) || 0,
        // @ts-ignore
        parseInt(claimToPreopen.claim[CLAIM.TOTAL_DECEASED_VICTIMS]) || 0,
        get(claimToPreopen, 'victims', []),
      ) &&
      !isUsageOpening
    ) {
      return setIsOpenAlert(true);
    }

    if (
      hasCourtCalendarFeature &&
      claimToPreopen.claim[CLAIM.IS_PROVISIONAL] === CHOICE.YES &&
      isProvisionalClaimExist === false
    ) {
      return setIsProvisionalReferenceAlertOpenned(true);
    }
    const validator = globalValidator.claimFormValidator(claimToPreopen);
    const validatorOpeningByUsage = globalValidator.claimFormValidatorOpeningByUsage(
      claimToPreopen,
    );

    let expectedValidatorVictims = get(validator, 'victims', []);
    expectedValidatorVictims = expectedValidatorVictims.map(
      (victim: VictimType) => {
        const expectedVictim = { ...victim };
        delete (expectedVictim as any).id;
        return expectedVictim;
      },
    );
    const expectedValidator = {
      claimCircumstances: get(validator, 'claimCircumstances'),
      claimInformations: get(validator, 'claimInformations'),
      claimDeclaration: get(validator, 'claimDeclaration'),
      opposingParty: get(validator, 'opposingParty'),
      fraudInfo:
        get(claimToPreopen, 'claim.suspicious') === CHOICE.YES
          ? get(validator, 'fraudInfo')
          : undefined,
    };
    if (!isUsageOpening) {
      setClaimValidation(validator);
    } else {
      setClaimValidation(validatorOpeningByUsage);
    }

    if (
      formsAreValidated(expectedValidator) &&
      listOfFormsAreValidated(expectedValidatorVictims) &&
      isUniqueCinVictims(
        get(claimToPreopen, 'victims'),
        get(claimToPreopen, 'claim.lossDate'),
      ) &&
      !checkClassVictims(
        get(claimToPreopen, 'victims'),
        get(claimToPreopen, 'claim.lossDate'),
      ) &&
      !checkVictimsAge(
        get(claimToPreopen, 'victims'),
        get(claimToPreopen, 'claim.lossDate'),
      ) &&
      !isUsageOpening
    ) {
      isPreOpeningManager
        ? routesHelper.pushTo(PAGES.UPLOAD_DOCUMENTS_PREOPEN_MANAGER, {
            isUsageOpening: isUsageOpening,
          })()
        : !get(props, 'location.state.isFromSagis')
        ? routesHelper.pushTo(PAGES.UPLOAD_DOCUMENTS)()
        : routesHelper.pushTo(PAGES.UPLOAD_DOCUMENTS, {
            isFromSagis: true,
            procedureNatureId,
            procedureType,
            judicialProcedureId: get(
              claimToPreopen,
              'claim.judicialProcedureId',
              null,
            ),
          })();
    } else if (isUsageOpening && formsAreValidated(validatorOpeningByUsage)) {
      routesHelper.pushTo(PAGES.UPLOAD_DOCUMENTS_PREOPEN_MANAGER, {
        isUsageOpening: isUsageOpening,
      })();
    }
  };
  const isOpeningManager =
    userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) &&
    !userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER) &&
    featureService.hasFeature(FEATURES.OPEN_CLAIM_BY_OPENING_MANAGER);

  return (
    <div>
      <Stepper
        isPreOpeningManager
        isBasicManagement={get(props, 'location.state.isFromSagis')}
      />
      <StyledDiv>
        {translate({ ...messages.preOpeningPageTitle }, props)}
      </StyledDiv>
      {isOpenAlert && (
        <Alert
          severity="error"
          style={{ marginBottom: '16px' }}
          onClose={() => {
            setIsOpenAlert(false);
          }}
        >
          {translate(messages.preOpeningPageAlertError, props)}
        </Alert>
      )}
      {featureService.hasFeature(FEATURES.COURT_CALENDAR) &&
        isProvisionalReferenceAlertOpenned && (
          <Alert
            severity="error"
            style={{ marginBottom: '16px' }}
            onClose={() => {
              setIsProvisionalReferenceAlertOpenned(false);
            }}
          >
            {translate(
              { ...messages.preOpeningPageProvisionalReferenceAlertError },
              props,
            )}
          </Alert>
        )}
      <ClaimDeclaration
        listings={props.listings}
        updateFilde={handleChange}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        policyInfo={props.policyInfo}
        claimDeclarationValidation={get(
          claimValidation,
          'claimDeclaration',
          {},
        )}
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        selectedClaim={props.selectedClaim}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
      />
      <ClaimInformation
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        policyInfo={props.policyInfo}
        updateFilde={handleChange}
        listings={props.listings}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        claimInformationsValidation={get(
          claimValidation,
          'claimInformations',
          {},
        )}
        stakeholders={[]}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
      />
      {featureService.hasFeature(FEATURES.OPPOSING_PARTY) && (
        <OpposingParty
          listings={props.listings}
          setClaimToPreopen={setClaimToPreopen}
          claimToPreopen={claimToPreopen}
          opposingPartyValidation={get(claimValidation, 'opposingParty', {})}
          sagisOpposingParties={extractOpposingParties(
            get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []),
          )}
        />
      )}
      <ClaimCircumstances
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        updateFilde={handleChange}
        listings={props.listings}
        claimToPreopen={claimToPreopen}
        storedClaimToPreopen={props.storedClaimToPreopen}
        claimCircumstancesValidation={get(
          claimValidation,
          'claimCircumstances',
          {},
        )}
        claimCircumstancesSagis={{}}
        procedureType={procedureType}
        handleChangeProcedureType={handleChangeProcedureType}
        procedureNatureId={procedureNatureId}
        handleChangeProcedureNature={handleChangeProcedureNature}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
        setIsSearchExecuted={setIsSearchExecuted}
        isSearchExecuted={isSearchExecuted}
        setClaim={setClaim}
      />
      <VictimCard
        isRecapPage={false}
        isReadOnly={false}
        isManager={false}
        claimToPreopen={claimToPreopen}
        listings={props.listings}
        updateFilde={handleChange}
        storedClaimToPreopen={props.storedClaimToPreopen}
        victimsValidation={get(claimValidation, 'victims', [])}
        policyInfo={props.policyInfo}
        isBasicManagement={get(props, 'location.state.isFromSagis')}
      />
      {featureService.hasFeature(FEATURES.FRAUDE_CORPO) && (
        <FraudeInformation
          isRecapPage={false}
          updateFilde={handleChange}
          claimToPreopen={claimToPreopen}
          storedClaimToPreopen={props.storedClaimToPreopen}
          policyInfo={props.policyInfo}
          listings={props.listings}
          fraudInfoValidation={get(claimValidation, 'fraudInfo', {})}
        />
      )}
      <NavigationButtons
        forwardClick={handleNextPageClick}
        onPreviousClick={
          isOpeningManager
            ? routesHelper.pushTo(PAGES.MANAGER_HOME)
            : isPreOpeningManager
            ? routesHelper.pushTo(PAGES.IDENTIFICATION_PREOPEN_MANAGER)
            : routesHelper.pushTo(PAGES.IDENTIFICATION)
        }
        disabledMoveButton={
          hasCourtCalendarFeature &&
          claimToPreopen.claim[CLAIM.IS_PROVISIONAL] === CHOICE.YES &&
          !isSearchExecuted
        }
      />
    </div>
  );
};

const mapDispatchToProps = { setClaim };

const mapStateToProps = createStructuredSelector({
  listings: makeSelectListings(),
  storedClaimToPreopen: makeSelecClaimToPreopen(),
  policyInfo: makeSelectSearchClaims(),
  selectedClaim: makeSelectedClaims(),
  corporealLawyers: makeSelectCorporealLawyers(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(PreOpeningPage));
